// A game's state, the changes that can be made to it, and the scoring. Shared by the server (which
// validates and applies changes) and the browser (which applies its own changes before the server
// confirms them).
//
// Each team shares one glass (or several) per hole. Every sip counts, whoever takes it; the team adds
// up its own sips and penalties and enters one score per hole. Players are names only.
import { BONUS_HOLE, HOLE_COUNT, HOLES } from './course.ts';

export interface Team {
  name: string;
  /** Player names; '' = not filled in yet. The number of players is set when the game is created. */
  players: string[];
  /** The team's score per hole, as entered; null = not scored yet. */
  scores: (number | null)[];
  /** First through the door at the last hole: −1 on that hole. */
  bonus: boolean;
}

export interface GameState {
  teams: Team[];
}

/** One change. Values are absolute ("set to 3"), so the last change to reach the server wins. */
export type Op =
  | { t: 'score'; team: number; hole: number; v: number | null }
  | { t: 'team'; team: number; name: string }
  | { t: 'player'; team: number; player: number; name: string }
  | { t: 'bonus'; team: number; v: boolean };

export const MAX_NAME = 30;
export const MAX_STROKES = 99;
export const MIN_TEAM_SIZE = 1;
export const MAX_TEAM_SIZE = 8;
export const MIN_TEAMS = 2;
export const MAX_TEAMS = 8;

/** A name as typed, cleaned: no control characters, single spaces, at most MAX_NAME characters. */
export function cleanName(value: unknown, fallback: string): string {
  if (typeof value !== 'string') return fallback;
  const clean = Array.from(value.replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u2028-\u202e\u2066-\u2069]/g, '').replace(/\s+/g, ' ').trim())
    .slice(0, MAX_NAME)
    .join('')
    .trim();
  return clean || fallback;
}

const isScore = (value: unknown): value is number => Number.isInteger(value) && (value as number) >= 1 && (value as number) <= MAX_STROKES;

export function freshState(teamSizes: readonly number[]): GameState {
  return {
    teams: teamSizes.map((size, t) => ({
      name: `Team ${t + 1}`,
      players: Array<string>(size).fill(''),
      scores: Array<number | null>(HOLE_COUNT).fill(null),
      bonus: false,
    })),
  };
}

/** A typed score: '' = empty (null), 1-99 = that score, anything else = undefined (not allowed). */
export function parseScore(text: string): number | null | undefined {
  const t = text.trim();
  if (t === '') return null;
  if (!/^\d{1,2}$/.test(t)) return undefined;
  const v = Number(t);
  return isScore(v) ? v : undefined;
}

/**
 * A stored state in the current shape. Games from before the shared glass kept a score per player:
 * each hole becomes the score it showed then (the best three, without the bonus; empty until every
 * player had scored), and the default "Speler n" names become empty.
 */
export function normalizeState(raw: unknown): GameState {
  const teams = raw && typeof raw === 'object' ? (raw as { teams?: unknown }).teams : undefined;
  if (!Array.isArray(teams)) return { teams: [] };
  return {
    teams: teams.map((value: unknown, i): Team => {
      const t = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>;
      const rawPlayers: unknown[] = Array.isArray(t.players) ? t.players : [];
      const players = rawPlayers.map((p) => {
        const name = typeof p === 'string' ? p : p && typeof p === 'object' ? (p as { name?: unknown }).name : '';
        const clean = cleanName(name, '');
        return /^Speler( \d+)?$/.test(clean) ? '' : clean;
      });
      const scores = Array.from({ length: HOLE_COUNT }, (_, h): number | null => {
        if (Array.isArray(t.scores)) return isScore(t.scores[h]) ? t.scores[h] : null;
        const vals = rawPlayers.map((p) => {
          const s = p && typeof p === 'object' ? (p as { s?: unknown }).s : undefined;
          return Array.isArray(s) && isScore(s[h]) ? (s[h] as number) : null;
        });
        if (vals.length === 0 || vals.some((v) => v === null)) return null;
        const best = (vals as number[]).sort((a, b) => a - b).slice(0, 3);
        return Math.min(best.reduce((a, b) => a + b, 0), MAX_STROKES);
      });
      return { name: cleanName(t.name, `Team ${i + 1}`), players, scores, bonus: t.bonus === true };
    }),
  };
}

/** Team sizes such as "3,3,4", or null when not allowed. */
export function parseTeamSizes(text: string): number[] | null {
  if (!/^\d+(,\d+)*$/.test(text.trim())) return null;
  const sizes = text.trim().split(',').map(Number);
  if (sizes.length < MIN_TEAMS || sizes.length > MAX_TEAMS) return null;
  if (sizes.some((n) => n < MIN_TEAM_SIZE || n > MAX_TEAM_SIZE)) return null;
  return sizes;
}

const isIndex = (value: unknown, length: number): value is number => Number.isInteger(value) && (value as number) >= 0 && (value as number) < length;

/** `value` as a change that is valid for `state`, or null. */
export function parseOp(value: unknown, state: GameState): Op | null {
  if (!value || typeof value !== 'object') return null;
  const o = value as Record<string, unknown>;
  if (!isIndex(o.team, state.teams.length)) return null;
  const team = state.teams[o.team]!;
  switch (o.t) {
    case 'score':
      // A score per player (from before the shared glass) is refused: it is not the team's score.
      if ('player' in o || !isIndex(o.hole, HOLE_COUNT)) return null;
      if (o.v !== null && !isScore(o.v)) return null;
      return { t: 'score', team: o.team, hole: o.hole, v: o.v };
    case 'team':
      return typeof o.name === 'string' ? { t: 'team', team: o.team, name: cleanName(o.name, 'Team') } : null;
    case 'player':
      if (!isIndex(o.player, team.players.length) || typeof o.name !== 'string') return null;
      return { t: 'player', team: o.team, player: o.player, name: cleanName(o.name, '') };
    case 'bonus':
      return typeof o.v === 'boolean' ? { t: 'bonus', team: o.team, v: o.v } : null;
    default:
      return null;
  }
}

/** Applies a valid change in place. */
export function applyOp(state: GameState, op: Op): void {
  const team = state.teams[op.team];
  if (!team) return;
  switch (op.t) {
    case 'score':
      if (op.hole >= 0 && op.hole < HOLE_COUNT) team.scores[op.hole] = op.v;
      return;
    case 'team':
      team.name = op.name;
      return;
    case 'player':
      if (op.player >= 0 && op.player < team.players.length) team.players[op.player] = op.name;
      return;
    case 'bonus':
      team.bonus = op.v;
      return;
  }
}

export function cloneState(state: GameState): GameState {
  return JSON.parse(JSON.stringify(state)) as GameState;
}

/** The names filled in so far, for showing next to the team name. */
export function playerNames(team: Team): string {
  return team.players.filter((n) => n !== '').join(', ');
}

// ---------------------------------------------------------------- scoring

/** The team's score on hole `h`: what the team entered, −1 at the last hole with the bonus. Null until entered. */
export function holeScore(team: Team, h: number): number | null {
  const v = team.scores[h] ?? null;
  if (v === null) return null;
  return h === BONUS_HOLE && team.bonus ? v - 1 : v;
}

export interface Totals {
  tot: number;
  /** Strokes against par over the holes played. */
  diff: number;
  played: number;
}

export function totals(team: Team): Totals {
  let tot = 0;
  let par = 0;
  let played = 0;
  for (let h = 0; h < HOLE_COUNT; h++) {
    const s = holeScore(team, h);
    if (s !== null) {
      tot += s;
      par += HOLES[h]!.par;
      played++;
    }
  }
  return { tot, diff: tot - par, played };
}

export interface BoardRow extends Totals {
  team: Team;
  index: number;
}

/** The leaderboard, best first; teams that have not played a hole go last. */
export function leaderboard(state: GameState): BoardRow[] {
  return state.teams
    .map((team, index) => ({ team, index, ...totals(team) }))
    .sort((a, b) => (a.played && b.played ? a.diff - b.diff : b.played - a.played));
}

export function holeDone(state: GameState, h: number): boolean {
  return state.teams.every((t) => holeScore(t, h) !== null);
}

export const fmtDiff = (d: number): string => (d === 0 ? 'even' : d > 0 ? `+${d}` : `−${Math.abs(d)}`);
